import { State } from '@meraki-internal/state';
import { WithLinks } from '../../caremap/WithLinks';
import { ICareMap } from '../../caremap/ICareMap';
import { CaremapNotesModelFactory } from './CaremapNotesModelFactory';
import { ICaremapNote, ICaremapNoteResponse, INoteReactions } from './NotesClient';
import { ReactionsModel } from '../../community/reactions/ReactionsModel';
import { IResourceChangedSocketMessage } from '../../web-sockets/SocketState';
import { ICareMapNoteTarget } from './ICareMapNoteTarget';
import { isExpectedFetchError } from '../../support/isExpectedFetchError';
import { RecentLocalDeletions } from '../../support/RecentLocalDeletions';

/**
 * The server has told us this note is not ours to show: deleted, or private to someone else. A
 * distinct type because it is an answer rather than a failure, so it belongs in the UI as an
 * explanation and never in Sentry.
 */
export class NoteUnavailableError extends Error {
    constructor(public noteId: string) {
        super(`note ${noteId} is not available to this user`);
        this.name = 'NoteUnavailableError';
    }
}

export class NotesModel extends State<Record<string, never>> {
    constructor(
        private careMap: WithLinks<ICareMap>,
        private factory: CaremapNotesModelFactory
    ) {
        super({});
        this.reactions = this.factory.reactionsFactory.create(this);
    };

    private notes: ICaremapNoteResponse[] = [];
    reactions: ReactionsModel;

    hasLoaded = false;

    onWebsocketMessage = async (message: IResourceChangedSocketMessage) => {
        // triggered when someone reacts to a note
        const reactedNote = this.notes.find(n => n.links.reactions.href.toLowerCase() === message.resourceToRefresh.toLowerCase());
        if (reactedNote){
            try {
                reactedNote.reactions = await this.factory.notesClient.getNoteReactions(reactedNote);
                this.setState({ });
            } catch (err: any) {
                // a deleted note (404) or aborted/offline fetch is expected here — alert only on genuine failures
                if (!isExpectedFetchError(err)) {
                    this.factory.logger.error(`NotesModel.onWebsocketMessage failed updating reactions for noteId ${reactedNote.noteId}. Inner error: ${err.toString()}`);
                }
            };
            return;
        }

        // triggered when a note we already have changes (recording state, a co-owner's edit)
        // or is deleted
        const updatedNote = this.notes.find(n => n.links.self.href === message.resourceToRefresh);
        if (updatedNote) {
            if (message.resourceChangeType === 'deleted') {
                this.localDeleteNote(updatedNote);
                return;
            }
            try {
                await this.refresh(message.resourceToRefresh);
            } catch (err: any) {
                // a note we currently hold can become invisible to us: a co-owner deleted it (404)
                // or made it private when we lack get-private (403). Either way it must disappear
                // from our list immediately — important when sensitive content is belatedly marked
                // private. A transient/aborted fetch (no such status) is left alone; reconcile catches up.
                if (err && (err.status === 403 || err.status === 404)) {
                    this.localDeleteNote(updatedNote);
                    return;
                }
                if (!isExpectedFetchError(err)) {
                    this.factory.logger.error(`NotesModel.onWebsocketMessage failed updating recording for noteId ${updatedNote.noteId}. Inner error: ${err.toString()}`);
                }
            };
            return;
        }

        // a note self-URL we don't yet have in the list: either a co-owner added a note
        // ('changed') or removed one ('deleted'). Only react if it belongs to THIS caremap.
        const noteUrlRegex = /^\/users\/[^/]*\/caremaps\/([^/]*)\/notes\/([^/]*)$/;
        const match = noteUrlRegex.exec(message.resourceToRefresh);
        if (!match) {
            return;
        }
        const careMapId = match[1];
        const noteId = match[2];
        if (careMapId !== this.careMap.careMapId) {
            return;
        }

        if (message.resourceChangeType === 'changed') {
            // a co-owner added (or made public) a note we don't have yet — fetch and insert it.
            // Deliberately NOT checked against recently-deleted: this frame is how a note shared
            // back to us reappears, and unlike the catch-up merge it never runs again, so declining
            // it would drop the note until some later catch-up rather than merely delaying it.
            try {
                const added = await this.factory.notesClient.getNote({ href: message.resourceToRefresh });
                if (!this.notes.some(n => n.noteId === added.noteId)) {
                    this.notes.push(added);
                    this.setState({ });
                }
            } catch (err: any) {
                // a private note this co-owner can't see (403) or one already deleted (404) is expected:
                // it simply doesn't appear. Alert only on genuine failures.
                if (!isExpectedFetchError(err)) {
                    this.factory.logger.error(`NotesModel.onWebsocketMessage failed fetching new note ${noteId}. Inner error: ${err.toString()}`);
                }
            }
            return;
        }

        if (message.resourceChangeType === 'deleted') {
            // a co-owner deleted a note; remove locally if present (idempotent). Only noteId is
            // needed by localDeleteNote, so a partial note is sufficient here.
            this.localDeleteNote({ noteId } as ICaremapNote);
            return;
        }
    };

    load = async () => {
        const notes = await this.factory.notesClient.getNotesForCareMap(this.careMap);
        this.notes = notes;
        this.hasLoaded = true;
        this.setState({});
    };

    // In-flight fetches keyed by note id, so concurrent views asking for the same note share one
    // request rather than racing each other into the list.
    private inFlightNoteFetches = new Map<string, Promise<ICaremapNoteResponse>>();

    /**
     * Resolve a single note by id, fetching it if the list does not have it. The list is loaded in
     * bulk and only converges again on foreground reconcile or a websocket frame, so a note created
     * since the last load is absent until one of those happens. A view handed nothing but a note id
     * (a deep link, a push notification) has to be able to resolve it now instead of rendering an
     * empty body for as long as the gap lasts.
     *
     * Throws NoteUnavailableError when the note is not ours to show: either the server says so
     * (403/404), or we deleted it locally within the suppression window, including while this fetch
     * was in flight. Any other failure propagates as itself.
     */
    fetchNoteById = async (noteId: string): Promise<ICaremapNoteResponse> => {
        const alreadyHave = this.getNote(noteId);
        if (alreadyHave) {
            return alreadyHave;
        }

        // we applied a delete for this note moments ago, so the server still returning it means its
        // read is behind us rather than that the note is back. Answering with it would both show the
        // user a note they deleted and push it back into the list.
        if (this.recentlyDeleted.isRecent(noteId)) {
            throw new NoteUnavailableError(noteId);
        }

        const inFlight = this.inFlightNoteFetches.get(noteId);
        if (inFlight) {
            return inFlight;
        }

        const fetch = (async () => {
            try {
                const fetched = await this.factory.notesClient.getNoteById({ careMap: this.careMap, noteId });

                // checked again after the await, not only before it: the delete can land while this
                // read is in flight, and this path serves deep links and push taps, which is exactly
                // where a fresh delete is most likely to race the fetch.
                if (this.recentlyDeleted.isRecent(noteId)) {
                    throw new NoteUnavailableError(noteId);
                }

                if (!this.notes.some(n => n.noteId === fetched.noteId)) {
                    this.notes.push(fetched);
                    this.setState({ });
                }
                return fetched;
            } catch (err: any) {
                // our own answer from the suppression check above — already the right type, and not
                // a failure, so it must pass through without being logged
                if (err instanceof NoteUnavailableError) {
                    throw err;
                }

                // 404 deleted, 403 private to someone else: the note is genuinely not ours to show,
                // so this is an answer rather than a failure and must not reach Sentry.
                if (err && (err.status === 403 || err.status === 404)) {
                    throw new NoteUnavailableError(noteId);
                }
                if (!isExpectedFetchError(err)) {
                    this.factory.logger.error(`NotesModel.fetchNoteById failed for noteId ${noteId}. Inner error: ${err.toString()}`);
                }
                throw err;
            } finally {
                this.inFlightNoteFetches.delete(noteId);
            }
        })();

        this.inFlightNoteFetches.set(noteId, fetch);
        return fetch;
    };

    refresh = async (resource: string) => {
        const updated = await this.factory.notesClient.getNote({ href: resource });
        const existing = this.notes.find(n => n.links.self.href === resource);
        if (existing) {
            Object.assign(existing, updated);
        } else if (!this.recentlyDeleted.isRecent(updated.noteId)) {
            // the note left the list while this fetch was in flight, and websocket frames are
            // dispatched without awaiting each other, so a 'changed' refresh and a 'deleted' run
            // concurrently: the delete lands first and this (possibly pre-delete) read would
            // otherwise push the note the user just watched disappear straight back.
            this.notes.push(updated);
        }
        this.setState({ });
    };

    // Catch-up merge used by the foreground/reconnect reconciler: re-fetch the server's notes and
    // converge the in-memory list by noteId (update changed in place, add new, drop removed) WITHOUT
    // resetting hasLoaded, so already-rendered note views never flash a skeleton.
    reconcile = async (): Promise<void> => {
        let server: ICaremapNoteResponse[];
        try {
            server = await this.factory.notesClient.getNotesForCareMap(this.careMap);
        } catch (err: any) {
            // a transient/aborted notes fetch is expected for this best-effort catch-up — it retries on
            // the next trigger. Alert only on genuine failures.
            if (!isExpectedFetchError(err)) {
                this.factory.logger.error(`NotesModel.reconcile failed to fetch notes: ${err.toString()}`);
            }
            return;
        }

        const serverById = new Map(server.map(n => [n.noteId, n]));
        const existingIds = new Set(this.notes.map(n => n.noteId));
        const reconciled: ICaremapNoteResponse[] = [];

        // update notes we already have in place (preserving object identity, like refresh()),
        // implicitly dropping any the server no longer returns
        for (const existing of this.notes) {
            const updated = serverById.get(existing.noteId);
            if (updated) {
                Object.assign(existing, updated);
                reconciled.push(existing);
            }
        }

        // append notes the server has that we don't, minus any we removed locally moments ago
        for (const serverNote of server) {
            if (!existingIds.has(serverNote.noteId) && !this.recentlyDeleted.isRecent(serverNote.noteId)) {
                reconciled.push(serverNote);
            }
        }

        this.notes = reconciled;
        this.setState({});
    };

    canEdit = (note: ICaremapNote) => {
        return note.links.self && (note.links.self.actions || []).includes('put-note');
    };

    canDelete = (note: ICaremapNote) => {
        return note.links.self && (note.links.self.actions || []).includes('delete-note');
    };

    addNewNote = async (note: ICaremapNote) => {
        const response = await this.factory.notesClient.addNoteToCareMap({
            careMap: this.careMap,
            note: {
                ...note,

                // a signal so the API knows notifications can go out
                // that we're not an old client that POSTed on the first few key strokes
                isComplete: true
            }
        });

        // when running locally with bypass, note will already have been added via websocket
        // so only add it here if not already in the list
        if (!this.notes.some(n => n.noteId === response.noteId)) {
            this.notes.push(response);
            this.setState({});
        }

        return response;
    };

    putNote = async (note: WithLinks<ICaremapNote>) => {
        const response = await this.factory.notesClient.updateNote(note);
        this.notes = [
            ...this.notes.filter(n => n.noteId !== note.noteId),
            response
        ];
        this.setState({});
    };

    deleteNote = async (note: WithLinks<ICaremapNote>, options?: { skipTracking?: boolean }) => {
        const deletedNote = this.notes.find(n=>n.noteId === note.noteId);
        await this.factory.notesClient.deleteNote(note);

        if (deletedNote && !options?.skipTracking) {
            this.factory.tracking.onDeletedNote(deletedNote);
        }

        this.localDeleteNote(note);
    };

    // Guards every path that merges a server note back into the list against a read that predates a
    // delete we have already applied. localDeleteNote is the single choke point for a note leaving
    // the list — this user's own delete, a collaborator's delete over the websocket, and a note
    // becoming invisible to us on a 403 — so recording it here covers all three.
    private recentlyDeleted = new RecentLocalDeletions();

    localDeleteNote = (note: ICaremapNote) => {
        this.notes = this.notes.filter(n => n.noteId !== note.noteId);
        this.recentlyDeleted.record(note.noteId);

        // we mutated state, let subscribers know
        this.setState({});
    };

    getNotes = (): ICaremapNoteResponse[] => {
        return this.notes;
    };

    getNotesForTarget = (noteTarget: ICareMapNoteTarget): ICaremapNote[] => {
        return this.notes.filter(n => this.isSameTarget(n.otherAttributes.noteTarget, noteTarget));
    };

    private isSameTarget = (l: ICareMapNoteTarget, r: ICareMapNoteTarget) => {
        return l.section === r.section && l.memberId === r.memberId;
    };

    getNote = (noteId: string): ICaremapNoteResponse | undefined => {
        return this.getNotes().find(n => n.noteId === noteId);
    };

    getAttachmentCount = () => {
        return this.notes.reduce((count, note) => {
            return count + note.attachments.length;
        }, 0);
    };

    updateLocalReactions = ({ note, reactions }: { note: ICaremapNote; reactions: INoteReactions; }) => {
        note.reactions = reactions;
        this.setState({});
    };

}
